import { User, UserIcon } from "lucide-react";
import Link from "next/link";

const VoterList = ({ voters, isAssembly }: { voters: any[]; isAssembly: boolean }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
        {voters.map((voter) => (
            <div key={voter.id} className="flex items-center gap-3 bg-white p-2 rounded-lg border border-slate-100">
                <img
                    src={voter.avatarUrl || voter.profiles?.avatar_url}
                    className="w-8 h-8 rounded-full border border-amber-100"
                    alt=""
                />
                <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                        {voter.rank && (
                            <span className="text-[8px] bg-blue-100 px-1 rounded-sm text-blue-800 font-bold uppercase">
                                {voter.rank}
                            </span>
                        )}
                        <Link
                            href={`/profile/${voter.username}`}
                            className="text-xs font-bold text-slate-700 hover:underline"
                        >
                            {voter.username}
                        </Link>
                    </div>
                    <span className={`text-[9px] font-bold uppercase ${voter.vote_type === 'upvote' ? 'text-green-600' : 'text-red-600'}`}>
                        {voter.vote_type === 'upvote' ? 'Aye' : 'No'}
                    </span>
                </div>
            </div>
        ))}
        {voters.length === 0 && <p className="text-xs text-slate-400 italic">No votes recorded yet.</p>}
    </div>
);

export default VoterList;

import React, { useState } from "react";

export const VotingRecordList = ({ voters, isAssembly, history = [], chamber = 'assembly' }: { voters: any[]; isAssembly: boolean; history?: any[]; chamber?: string }) => {
    const ayes = voters.filter((v) => v.vote_type === 'upvote').length;
    const noes = voters.filter((v) => v.vote_type === 'downvote').length;
    const totalVotes = ayes + noes;

    const [activeTab, setActiveTab] = useState<'votes' | 'history'>('votes');

    // 1. Parliamentary Logic Mapping
    const getRules = (type: string) => {
        switch (type) {
            case 'plus': return { q: 1 + 0.0, n: 3, d: 4, label: "Three-Fourths", chamberName: "Great Hall" };
            case 'pro': return { q: 1 + 0.00, n: 4, d: 5, label: "Four-Fifths", chamberName: "Grand Council" };
            case 'elite': return { q: 1 + 0.000, n: 1, d: 1, label: "Unanimous", chamberName: "Convention of Fathers", isUnanimous: true };
            default: return { q: 1, n: 2, d: 3, label: "Two-Thirds", chamberName: "General Assembly" };
        }
    };

    const rules = getRules(chamber);
    const quorum = rules.q;
    const quorumMet = totalVotes >= quorum;

    // Parliamentary Constants
    // const quorum = 1;
    // const quorumMet = totalVotes >= quorum;
    // const majorityThreshold = quorumMet ? Math.floor(totalVotes / 2) + 1 : Math.floor(quorum / 2) + 1;
    // let twoThirdsThreshold = quorumMet ? Math.ceil((totalVotes * 2) / 3) : Math.ceil((quorum * 2) / 3);


    // If the thresholds are equal, increment the two-thirds requirement by one
    // if (twoThirdsThreshold === majorityThreshold) {
    //     twoThirdsThreshold++;
    // }


    // Threshold Calculations
    const majorityThreshold = quorumMet ? Math.floor(totalVotes / 2) + 1 : Math.floor(quorum / 2)+ 1;
    let highThreshold = rules.isUnanimous
        ? (quorumMet ? totalVotes : quorum)
        : Math.ceil((quorumMet ? totalVotes : quorum) * rules.n / rules.d);

    //if (highThreshold <= majorityThreshold) //highThreshold++; //&& !rules.isUnanimous
    //if (highThreshold === 1) highThreshold++;
    // Visual Percentage Helpers
    const quorumPercent = Math.min((totalVotes / quorum) * 100, 100);
    const highThresholdPercent = (highThreshold / (totalVotes > highThreshold ? totalVotes : highThreshold)) * 100;
    const ayesPercent = (ayes / (totalVotes > highThreshold ? totalVotes : highThreshold)) * 100;

    const formatStatus = (status: string) => {
        return status.replace(/_/g, ' ').toUpperCase();
    };

    const formatStatusText = (status: string) => {
        switch (status) {
            case 'in_effect':
            case 'accepted':
                return 'Favored';
            case 'rejected':
            case 'failed':
                return 'Opposed';
            case 'vetoed':
                return 'Vetoed';
            case 'tentatively_effected':
                return 'Tentatively Effective';
            case 'effected':
                return 'Effective';
            case 'proposed':
            case 'active':
            case 'pending':
                return 'Under Consideration';
            default:
                return status.replace(/_/g, ' ').toUpperCase();
        }
    };




    const formatStatusTextColor = (status: string) => {
        switch (status) {
            case 'in_effect':
            case 'accepted':
                return 'text-green-600';
            case 'rejected':
            case 'failed':
                return 'text-red-500';
            case 'vetoed':
                return 'text-red-900';
            case 'tentatively_effected':
                return 'text-green-700';
            case 'effected':
                return 'text-green-900';
            case 'proposed':
            case 'active':
            case 'pending':
                return 'text-amber-700';
            default:
                return 'text-slate-600';
        }
    };

    const formatStatusLine = (status: string) => {
        switch (status) {
            case 'in_effect':
            case 'accepted':
                return 'bg-green-100';
            case 'rejected':
            case 'failed':
                return 'bg-red-100';
            case 'vetoed':
                return 'bg-red-200';
            case 'tentatively_effected':
                return 'bg-green-200';
            case 'effected':
                return 'bg-green-300';
            case 'proposed':
            case 'active':
            case 'pending':
                return 'bg-amber-100';
            default:
                return 'bg-slate-100';
        }
    };

    const formatStatusOuterBackground = (status: string) => {
        switch (status) {
            case 'in_effect':
            case 'accepted':
                return 'bg-green-50';
            case 'rejected':
            case 'failed':
                return 'bg-red-50';
            case 'vetoed':
                return 'bg-red-100';
            case 'tentatively_effected':
                return 'bg-green-100';
            case 'effected':
                return 'bg-green-200';
            case 'proposed':
            case 'active':
            case 'pending':
                return 'bg-amber-50';
            default:
                return 'bg-slate-50';
        }
    };

    const formerStatusInnerBackground = (status: string) => {
        switch (status) {
            case 'in_effect':
            case 'accepted':
                return 'bg-green-400';
            case 'rejected':
            case 'failed':
                return 'bg-red-400';
            case 'vetoed':
                return 'bg-red-800';
            case 'tentatively_effected':
                return 'bg-green-600';
            case 'effected':
                return 'bg-green-800';
            case 'proposed':
            case 'active':
            case 'pending':
                return 'bg-amber-500';
            default:
                return 'bg-slate-400';
        }
    };

    const getFutureLawDate = (startDate: Date, category: string): string => {
        // Define the law thresholds (excluding Sundays)
        const thresholds: Record<string, number> = {
            'elite': 40,
            'pro': 30,
            'plus': 20,
            'assembly': 10,

        };

        // Default to 10 if category is unrecognized
        const targetDays = thresholds[category] || 10;

        let daysAdded = 0;
        let currentDate = new Date(startDate);

        // Loop until the required number of non-Sunday days are added
        while (daysAdded < targetDays) {
            currentDate.setDate(currentDate.getDate() + 1);

            // 0 is Sunday. Only increment daysAdded if it's Monday-Saturday.
            if (currentDate.getDay() !== 0) {
                daysAdded++;
            }
        }

        return currentDate.toLocaleDateString(undefined, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };


    //totalVotes >= quorum + 0 ? Math.ceil((totalVotes * 2) / 3) : Math.ceil(((quorum + 0) * 2) / 3); 

    // Adaptive Terminology
    const posTerm = isAssembly ? "Ayes" : "Yeas";
    const negTerm = isAssembly ? "Noes" : "Nays";
    const posSingular = isAssembly ? "Aye" : "Yea";
    const negSingular = isAssembly ? "No" : "Nay";



    // Result Declaration
    const resultDeclaration = ayes > noes ? `The ${posTerm} have it.` : `The ${negTerm} have it.`;

    return (
        <div className="w-full">
            {/* Parliamentary Declaration */}
            <div className="flex border-b border-slate-200 mb-6">
                <button
                    onClick={() => setActiveTab('votes')}
                    className={`pb-2 px-4 text-[10px] font-bold uppercase tracking-widest ${activeTab === 'votes' ? 'border-b-2 border-amber-800 text-amber-800' : 'text-slate-400'}`}
                >
                    Division of Votes
                </button>
                <button
                    onClick={() => setActiveTab('history')}
                    className={`pb-2 px-4 text-[10px] font-bold uppercase tracking-widest ${activeTab === 'history' ? 'border-b-2 border-amber-800 text-amber-800' : 'text-slate-400'}`}
                >
                    Resolution Log
                </button>
            </div>
            {activeTab === 'votes' ? (
                <>
                    <div className="text-center mb-6">
                        <h4 className={`text-xl font-serif font-bold italic tracking-wide ${ayes > noes ? "text-green-800" : "text-red-800"}`}>
                            {quorumMet && resultDeclaration}
                        </h4>

                        {/* Threshold Information{totalVotes}/{quorum} {quorumMet ? "Reached" : "Required"}
                (to Become Favored and Viewed by the Emperor)
                (to Popularly Effect)
                */}
                        <div className="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 font-sans">
                            <div className="flex items-center gap-1.5">
                                <span className="text-slate-400">Quorum:</span>
                                <span className={quorumMet ? "text-slate-800" : "text-amber-600"}>
                                    {quorum}
                                </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <span className="text-slate-400">Majority:</span>
                                <span className="text-slate-800">{majorityThreshold} {posTerm}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <span className="text-slate-400">{rules.label}:</span>
                                <span className="text-slate-800">{highThreshold} {posTerm}</span>
                            </div>
                        </div>
                    </div>

                    {/* Division Count Cards */}
                    <div className="flex justify-center gap-8 mb-6 pb-6 border-b border-slate-100">
                        <div className="text-center">
                            <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">
                                {posTerm}
                            </span>
                            <span className="text-3xl font-serif font-bold text-green-600">{ayes}</span>
                        </div>
                        <div className="text-center">
                            <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">
                                {negTerm}
                            </span>
                            <span className="text-3xl font-serif font-bold text-red-600">{noes}</span>
                        </div>
                    </div>

                    {/* Voter List */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                        {voters.map((voter, i) => (
                            <div key={i} className="flex items-center gap-3 bg-white p-3 rounded-xl border border-slate-100 shadow-sm transition-all hover:border-amber-200">
                                <div>
                                    {voter.imageUrl || voter.avatarUrl ? (
                                        <img
                                            src={voter.avatarUrl || voter.imageUrl || '/default-avatar.png'}
                                            className="w-10 h-10 rounded-full border border-amber-100 object-cover"
                                            alt={voter.username}
                                        />
                                    ) : (
                                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center border border-amber-100 text-slate-400">
                                            <UserIcon size={20} />
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col">
                                    <div className="flex items-center gap-2">
                                        {voter.rank && (
                                            <span className="text-[9px] bg-blue-100 px-1.5 py-0.5 rounded text-blue-800 font-bold uppercase tracking-tighter">
                                                {voter.rank === 'Founding Father' ? 'Founding Father' : voter.rank}
                                            </span>
                                        )}
                                        <Link href={`/profile/${voter.username}`} className="text-xs font-bold text-slate-800 hover:underline">
                                            {voter.username}
                                        </Link>
                                    </div>
                                    <div className="flex items-center gap-1.5 mt-0.5">
                                        <div className={`w-1.5 h-1.5 rounded-full ${voter.vote_type === 'upvote' ? 'bg-green-500' : 'bg-red-500'}`} />
                                        <span className={`text-[10px] font-bold uppercase tracking-widest ${voter.vote_type === 'upvote' ? 'text-green-600' : 'text-red-600'}`}>
                                            {voter.vote_type === 'upvote' ? posSingular : negSingular}
                                        </span>
                                        <span className="text-[9px] text-slate-400 font-sans ml-1">
                                            {new Date(voter.inserted_at || voter.created_at).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {voters.length === 0 && (
                            <p className="text-sm text-slate-500 italic p-4 text-center col-span-full">
                                No votes recorded. {/*  for this item. */}
                            </p>
                        )}
                    </div>
                </>
            ) : <>
                <div className="space-y-4">
                    {/* Static Start Entry */}
                    <div className="flex gap-4 items-start relative pb-4">
                        <div className="absolute left-[11px] top-[24px] bottom-0 w-[2px] bg-amber-100"></div>
                        <div className="z-5 w-6 h-6 rounded-full bg-amber-100 border-2 border-white flex items-center justify-center">
                            <div className="w-2 h-2 rounded-full bg-amber-600"></div>
                        </div>
                        <div className="flex-1">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Initial State</p>
                            <p className="text-sm font-serif font-bold text-slate-900">Proposed and <span className="text-amber-600">Under Consideration</span></p>
                        </div>
                    </div>

                    {/* Dynamic History Entries */}
                    {history.map((log, idx) => (
                        <div key={log.id} className="flex gap-4 items-start relative pb-4">
                            {idx !== history.length - 1 && (
                                <div className={`absolute left-[11px] top-[24px] bottom-0 w-[2px] ${formatStatusLine(log.new_status)}`}></div>
                            )}
                            <div className={`z-5 w-6 h-6 rounded-full ${formatStatusOuterBackground(log.new_status)} border-2 border-white flex items-center justify-center`}>
                                <div className={`w-2 h-2 rounded-full ${formerStatusInnerBackground(log.new_status)}`}></div>
                            </div>
                            <div className="flex-1">
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                    {new Date(log.created_at).toLocaleString()}
                                </p>
                                <p className="text-sm font-serif font-bold text-slate-900">
                                    Became <span className={`${formatStatusTextColor(log.new_status)}`}>
                                        {formatStatusText(log.new_status)}
                                    </span>
                                </p>
                                {log.new_status === 'tentatively_effected' && (
                                    <p className="text-[10px] mt-1 font-sans font-bold uppercase tracking-tight text-green-700 bg-green-50 inline-block px-2 py-0.5 rounded">
                                        Set to become law on {getFutureLawDate(new Date(log.created_at), chamber)} (Unless Vetoed)
                                    </p>
                                )}
                                {log.note && <p className="text-xs font-serifs font-light text-slate-400">
                                    {log.note}
                                </p>}
                            </div>
                        </div>
                    ))}

                    {history.length === 0 && (
                        <p className="text-xs text-slate-500 italic text-center py-4">No additional history recorded.</p>
                    )}
                </div>
            </>}

        </div>
    );
};

// (Unless Vetoed)
// (Unless Vetoed by Emperor)
//(Unless Emperor Vetoes)
//(Unless Emperor Vetoes or Loses Tentative Status)


/*<>
                  
                    <div className="text-center mb-8">
                        <span className="text-[10px] font-black uppercase tracking-[0.4em] text-amber-600/60 mb-2 block">{rules.chamberName} Rules</span>
                        <h4 className={`text-xl font-serif font-bold italic tracking-wide ${ayes > noes ? "text-green-800" : "text-red-800"}`}>
                            {quorumMet ? resultDeclaration : "Quorum Not Yet Reached"}
                        </h4>
                    </div>

                    <div className="max-w-md mx-auto mb-10 space-y-6">
                      
                        <div>
                            <div className="flex justify-between text-[9px] font-bold uppercase tracking-widest mb-1.5">
                                <span className={quorumMet ? "text-green-600" : "text-slate-400"}>Quorum Progress</span>
                                <span className="text-slate-600">{totalVotes} / {quorum} Votes</span>
                            </div>
                            <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden border border-slate-200">
                                <div 
                                    className={`h-full transition-all duration-500 ${quorumMet ? 'bg-green-500' : 'bg-amber-500'}`} 
                                    style={{ width: `${quorumPercent}%` }}
                                />
                            </div>
                        </div>

                     
                        <div className="relative pt-6">
                            <div className="flex justify-between text-[9px] font-bold uppercase tracking-widest mb-1.5">
                                <span className="text-slate-400">{posTerm} vs Thresholds</span>
                                <span className="text-green-700">{ayes} {posTerm} Cast</span>
                            </div>
                            
                            <div className="h-4 w-full bg-slate-100 rounded-md overflow-hidden border border-slate-200 relative">
                        
                                <div 
                                    className="absolute top-0 bottom-0 w-0.5 bg-slate-400 z-10" 
                                    style={{ left: `${(majorityThreshold / (totalVotes > highThreshold ? totalVotes : highThreshold)) * 100}%` }}
                                >
                                    <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[8px] whitespace-nowrap text-slate-500">Majority ({majorityThreshold})</span>
                                </div>

                               
                                <div 
                                    className="absolute top-0 bottom-0 w-1 bg-amber-600 z-10" 
                                    style={{ left: `${highThresholdPercent}%` }}
                                >
                                    <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[8px] whitespace-nowrap text-amber-700 font-black">{rules.label} ({highThreshold})</span>
                                </div>

                               
                                <div 
                                    className={`h-full transition-all duration-700 ${ayes >= highThreshold ? 'bg-green-600' : 'bg-green-400'}`} 
                                    style={{ width: `${ayesPercent}%` }}
                                />
                            </div>
                            <p className="text-[9px] text-center mt-6 text-slate-400 italic">
                                {ayes >= highThreshold 
                                    ? `Threshold met. Resolution is tentatively effective.` 
                                    : `Requires ${highThreshold - ayes} more ${posTerm} to reach ${rules.label} majority.`}
                            </p>
                        </div>
                    </div>

         
                    <div className="flex justify-center gap-12 mb-8">
                        <div className="text-center">
                            <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">{posTerm}</span>
                            <span className="text-4xl font-serif font-bold text-green-600">{ayes}</span>
                        </div>
                        <div className="text-center">
                            <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">{negTerm}</span>
                            <span className="text-4xl font-serif font-bold text-red-600">{noes}</span>
                        </div>
                    </div>

              
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                        {voters.map((voter, i) => (
                            <div key={i} className="flex items-center gap-3 bg-white p-3 rounded-xl border border-slate-100 shadow-sm">
                                <div className="w-10 h-10 rounded-full bg-slate-100 border border-amber-100 overflow-hidden flex items-center justify-center">
                                    {voter.avatarUrl || voter.imageUrl ? (
                                        <img src={voter.avatarUrl || voter.imageUrl} className="object-cover w-full h-full" alt="" />
                                    ) : <UserIcon size={20} className="text-slate-300" />}
                                </div>
                                <div className="flex flex-col">
                                    <Link href={`/profile/${voter.username}`} className="text-xs font-bold text-slate-800 hover:underline">{voter.username}</Link>
                                    <span className={`text-[10px] font-bold uppercase ${voter.vote_type === 'upvote' ? 'text-green-600' : 'text-red-600'}`}>
                                        {voter.vote_type === 'upvote' ? rules.isUnanimous ? "Affirm" : posSingular : negSingular}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </>*/